// =============================================================================
// selectors/bestOpps.ts — the seam between the Best Opps screen and everything
// underneath it.
// -----------------------------------------------------------------------------
// `ui/` may not import `domain/` (D47), so this is the only door. It also does
// the one thing a selector is for: it decides SCOPE.
//
// 🚨 AND THE SCOPE HERE IS "EVERYTHING", WHICH IS THE SURPRISE. Every other
// surface in this client is scoped by the global date range and the account
// filter. Best Opps is not — beta renders `state.bestOpps` whole, and Josh's
// screenshot proves it: entries from May, on screen, under a `This week` filter,
// beneath a heading that says "Best Opps of the Day".
//
// ⚠ That is stated HERE rather than left implicit in the tab, because "this
// selector applies no filter" is exactly the kind of omission that reads as a
// bug to the next person and gets 'fixed' into one.
// =============================================================================

import {
  bestOppCard, emptyBestOpp, sortBestOpps,
  type BestOpp, type BestOppCard,
} from '../domain/bestOpps';
import {
  deleteBestOpp, loadBestOpps, saveBestOpp, type SaveBestOppResult,
} from '../io/sync/bestOpps';
import { loadTradesInRange, type TradeTableRow } from '../io/sync/trades';
import { taggablePatterns, type PatternSummary } from '../io/sync/patterns';
import type { DayBoundaryMode } from '../domain/range';
import type { SupabaseClient } from '@supabase/supabase-js';

export type { BestOpp, BestOppCard, SaveBestOppResult };

/** A named pair so the tab does not have to zip two arrays by index. */
export interface BestOppRow {
  opp: BestOpp;
  card: BestOppCard;
}

/**
 * Every opp, newest first, each paired with its card view-model.
 *
 * @param patternName resolves `identity.patternId` to a display name. Returns
 * null for an unknown id — which happens legitimately: `pattern_id` is a foreign
 * key with ON DELETE SET NULL, so deleting a pattern leaves the opp intact with
 * a dangling-but-nulled link, and the card reads "(no pattern)".
 */
export function selectBestOppRows(
  opps: readonly BestOpp[] | null,
  patternName: (id: string) => string | null,
): BestOppRow[] {
  return sortBestOpps(opps ?? []).map((opp) => ({
    opp,
    card: bestOppCard(opp, patternName(opp.identity.patternId)),
  }));
}

export function newBestOpp(id: string, todayIso: string): BestOpp {
  return emptyBestOpp(id, todayIso);
}

export function fetchBestOpps(sb: SupabaseClient): Promise<BestOpp[]> {
  return loadBestOpps(sb);
}

export function storeBestOpp(
  sb: SupabaseClient, userId: string, o: BestOpp,
): Promise<SaveBestOppResult> {
  return saveBestOpp(sb, userId, o);
}

export function removeBestOpp(sb: SupabaseClient, id: string): Promise<SaveBestOppResult> {
  return deleteBestOpp(sb, id);
}

/**
 * The trades of ONE day, for the "Linked Trade (same day)" picker.
 *
 * ── 🚨 THE ONE PLACE THIS TAB'S SCOPE IS DECIDED, AND IT IS NOT "EVERYTHING" ──
 *
 * The note at the top of this file is about the OPPS: they are unfiltered on
 * purpose. This is the opposite and stays that way — an opportunity is linked to
 * the trade it became, so the menu offers that day and no other. A picker over
 * the whole book would be thousands of rows and would invite linking an opp to a
 * trade from a different session entirely.
 *
 * ⚠ SESSION-AWARE, WHICH IS A DEPARTURE FROM BETA AND THE POINT OF ROUTING IT
 * HERE. Beta filters `state.trades` on the RAW `t.core.date`. This client has a
 * day-boundary mode, and `loadTradesInRange` already applies it — so under
 * `session` an 18:05 trade belongs to the next trading day on Day Overview, on
 * the Calendar, and now here too. Matching beta literally would have made this
 * one control disagree with every other surface about which trades are "that
 * day", which is the exact failure the single-source rule exists to prevent.
 *
 * ⚠ Both bounds are the SAME date. `loadTradesInRange` pads its query for the
 * session window and then filters back to the bounds, so a one-day range is a
 * supported case rather than a degenerate one.
 */
export function fetchTradesOn(
  sb: SupabaseClient, date: string, mode: DayBoundaryMode, sessionTz: string,
): Promise<readonly TradeTableRow[]> {
  return loadTradesInRange(sb, { startDate: date, endDate: date }, mode, sessionTz)
    .then((r) => r.rows);
}

/**
 * The patterns a trade — or an opp — may be tagged with, plus whichever one is
 * ALREADY chosen.
 *
 * 🚨 `keepId` IS NOT A CONVENIENCE. `taggablePatterns` hides a pattern that is
 * still being tested, and an opp saved while its pattern was live must not
 * silently lose it the day that pattern goes back into testing: the select would
 * render with no matching option, fall back to "(none)", and the next save would
 * write that blank over a real answer. The user would never see it happen.
 *
 * ⚠ Appended rather than prepended, so the menu's order is the shared one and
 * the odd entry is the one at the end.
 */
export function pickablePatterns(
  patterns: readonly PatternSummary[] | null, keepId: string | null | undefined,
): readonly PatternSummary[] {
  const allowed = taggablePatterns(patterns);
  if (!keepId) return allowed;
  if (allowed.some((p) => p.id === keepId)) return allowed;
  const kept = (patterns ?? []).find((p) => p.id === keepId);
  return kept ? [...allowed, kept] : allowed;
}
